import React, { useEffect, useState } from 'react';
import { Check, Copy, Download, ExternalLink, Facebook, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api, { formatError } from '@/lib/api';

const STEPS = [
  { key: 'details', label: 'Make the Page' },
  { key: 'whatsapp', label: 'Link WhatsApp' },
  { key: 'portfolio', label: 'Business portfolio' },
  { key: 'connect', label: 'Connect' },
  { key: 'approve', label: 'Approve' },
];
const FIELDS = [['name', 'Page name'], ['category', 'Category'], ['bio', 'Bio']];

function CopyRow({ label, value }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error('Copy didn’t work. Select the text and copy it yourself.'); }
  };
  return (
    <div className="flex items-start gap-2 p-2.5 rounded-lg border-2 border-ink/15 min-w-0">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold break-words">{value}</p>
      </div>
      <Button size="sm" variant="outline" onClick={copy} aria-label={`Copy ${label}`}>
        {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
}

const download = async (kind) => {
  try {
    const { data } = await api.get(`/api/facebook/page-setup/${kind}.jpg`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = Object.assign(document.createElement('a'), { href: url, download: `facebook-page-${kind}.jpg` });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) { toast.error(formatError(err.response?.data?.detail) || 'Couldn’t make the picture. Try again.'); }
};

const External = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer">
    <Button size="sm" variant="outline">{children} <ExternalLink className="w-3 h-3 ml-1" /></Button>
  </a>
);

/** The last step for any Page we asked to run ads for: the owner approves our request in Meta Business.
 * Used by the guide and for a Page connected without it, so it looks the same either way. */
export function ApproveStep({ page, onCheck, checking = false }) {
  return (
    <div className="space-y-3" data-testid="approve-step">
      {page && <p className="text-sm">Your Page: <b>{page.page_name}</b></p>}
      {page?.share_error && <p className="text-xs text-amber-700 dark:text-amber-400">{page.share_error}</p>}
      <ol className="list-decimal pl-5 text-xs text-muted-foreground space-y-1">
        <li>Open Meta Business and go to Settings → Requests.</li>
        <li>Approve the request from Rayogent.</li>
        <li>Come back and tap <b>Check again</b>.</li>
      </ol>
      <div className="flex flex-wrap gap-2">
        <External href="https://business.facebook.com/settings/requests">Open Meta Business</External>
        <Button size="sm" className="gradient-bg" onClick={onCheck} disabled={checking}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${checking ? 'animate-spin' : ''}`} /> Check again
        </Button>
      </div>
    </div>
  );
}

/** For an owner with no Facebook Page: the agent writes the Page's details and makes its pictures; the owner
 * pastes them into Facebook (no app can make a Page for them), links WhatsApp, adds the Page to a business
 * portfolio and logs in again, the new Page is picked for them, and they approve our access request. The step
 * is saved, so they can come back. page/onCheck: the picked Page and "Check again" (from FacebookConnect). */
export default function PageSetupGuide({ onLogin, page = null, onCheck, checking = false, profileVersion = 0 }) {
  const [guide, setGuide] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState('');

  useEffect(() => {
    api.get('/api/facebook/page-setup').then(({ data }) => setGuide(data))
      .catch(err => toast.error(formatError(err.response?.data?.detail) || 'Couldn’t load the guide. Try again.'));
  }, [profileVersion]); // the business details were saved: the Page's details are rewritten from them
  const savePhone = async () => {
    setBusy(true);
    try { setGuide((await api.post('/api/facebook/page-setup/phone', { phone })).data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setBusy(false);
  };
  const go = async (step) => {
    setBusy(true);
    try { setGuide((await api.post('/api/facebook/page-setup/step', { step })).data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setBusy(false);
  };

  if (!guide) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Writing your Page’s details…</div>;
  const d = guide.details;
  const at = Math.max(0, STEPS.findIndex(s => s.key === guide.step));
  const next = (
    <Button size="sm" className="gradient-bg" disabled={busy} onClick={() => go(STEPS[at + 1].key)}>
      {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Done, next step
    </Button>
  );

  return (
    <div className="space-y-4" data-testid="page-setup-guide">
      <div>
        <p className="text-sm font-semibold">Let’s make your business a Facebook Page</p>
        <p className="text-xs text-muted-foreground">We’ve written everything. You paste it in, about 5 minutes in all. Ads from your own Page get more trust, and WhatsApp ads need one.</p>
      </div>
      <ol className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 text-[11px]">
        {STEPS.map((s, i) => (
          <li key={s.key} className={`px-2 py-1 rounded-md border-2 text-center ${i === at ? 'border-ink bg-butter font-semibold' : i < at ? 'border-money/40 text-money' : 'border-ink/15 text-muted-foreground'}`}>
            {i < at ? '✓ ' : `${i + 1}. `}{s.label}
          </li>
        ))}
      </ol>

      {guide.step === 'details' && (
        <div className="space-y-3">
          {!d.name && (
            <p className="text-xs p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400" data-testid="profile-first">
              Fill in your business details above and tap Save first. We write your Page from them.
            </p>
          )}
          <div className="space-y-2">{FIELDS.map(([k, label]) => <CopyRow key={k} label={label} value={d[k]} />)}</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => download('picture')}><Download className="w-3.5 h-3.5 mr-1" /> Profile picture</Button>
            <Button size="sm" variant="outline" onClick={() => download('cover')}><Download className="w-3.5 h-3.5 mr-1" /> Cover photo</Button>
          </div>
          <ol className="list-decimal pl-5 text-xs text-muted-foreground space-y-1">
            <li>Download the two pictures, then tap <b>Open Facebook</b>.</li>
            <li>Paste the Page name, type the category and pick the closest one, and paste the bio.</li>
            <li>Tap Create Page, then add the profile picture and cover photo.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <External href="https://www.facebook.com/pages/create">Open Facebook</External>
            {next}
          </div>
          <button className="text-xs text-muted-foreground underline" disabled={busy} onClick={() => go('connect')}>Already have a Page? Connect it</button>
        </div>
      )}

      {guide.step === 'whatsapp' && (
        <div className="space-y-3">
          {d.phone ? <CopyRow label="Your WhatsApp number" value={d.phone} /> : (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold">Which number do customers WhatsApp you on?</p>
              <div className="flex gap-2">
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="98765 43210" inputMode="tel" aria-label="WhatsApp number" />
                <Button size="sm" className="gradient-bg" disabled={busy || !phone.trim()} onClick={savePhone}>Save</Button>
              </div>
            </div>
          )}
          <ol className="list-decimal pl-5 text-xs text-muted-foreground space-y-1">
            <li>On Facebook, switch to your new Page (tap your profile picture, then the Page).</li>
            <li>Go to Settings → Linked accounts → WhatsApp.</li>
            <li>Enter your WhatsApp number and type the code WhatsApp sends you.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <External href="https://www.facebook.com/settings">Open Facebook settings</External>
            {next}
          </div>
          <button className="text-xs text-muted-foreground underline" disabled={busy} onClick={() => go('portfolio')}>Skip: I don’t want WhatsApp ads</button>
        </div>
      )}

      {guide.step === 'portfolio' && (
        <div className="space-y-3">
          <ol className="list-decimal pl-5 text-xs text-muted-foreground space-y-1">
            <li>Open Meta Business. If it asks, create a business portfolio with your business’s name.</li>
            <li>Go to Settings → Accounts → Pages → Add → Add a Page.</li>
            <li>Pick your new Page.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <External href="https://business.facebook.com/settings/pages">Open Meta Business</External>
            {next}
          </div>
        </div>
      )}

      {guide.step === 'connect' && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Log in with Facebook again. When Facebook asks which Pages Rayogent can use, tick your new Page. We’ll pick it for you here.</p>
          <p className="text-xs text-muted-foreground">If Facebook just says “Continue as…”, tap <b>Edit access</b> first, so you can tick the new Page.</p>
          <Button onClick={onLogin} className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white">
            <Facebook className="w-4 h-4 mr-1" /> Continue with Facebook
          </Button>
        </div>
      )}

      {guide.step === 'approve' && <ApproveStep page={page} onCheck={onCheck} checking={checking} />}

      {at > 0 && guide.step !== 'approve' && <button className="text-xs text-muted-foreground underline" disabled={busy} onClick={() => go(STEPS[at - 1].key)}>Back a step</button>}
    </div>
  );
}
